import {
  ADDITIONAL_SELLER_ID_TYPES,
  ADDITIONAL_BUYER_ID_TYPES,
  PAYMENT_METHOD,
  NOTE_ISSUANCE_REASONS,
  PREVIOUS_INVOICES,
  EXEMPTION_REASON_CODES,
  ZERO_RATING_REASON_CODES,
  OUT_OF_SCOPE_REASON_CODES,
} from '../../../../../config/invoice/options';
import {
  onValuesBlur,
  onVatRateChange,
  setOriginalInvoiceGross,
  getTaxTypes,
} from '../../../../../helpers/invoiceHelpers';
import moment from 'moment';
import { FIELDS_MAX_LENGTH } from './validations';
import { getInputType } from '../helpers';
import { CURRENCY_LIST } from '../../../../../config/invoice/constants';
import { getLocalizedCurrencyLabel } from '../../../../../helpers/invoiceHelpers';
import i18n from '../../../../../config/i18n';

export const INVOICE_TYPE = {
  INVOICE: 'invoice',
  CREDIT: 'credit',
  DEBIT: 'debit',
};

export const invoicingPeriodInputs = ({
  values,
  setFieldValue,
  setFieldTouched,
}) => [
  {
    name: 'invoicingPeriodStartDate',
    type: 'datepicker',
    label: 'createInvoice.invoicing_period_start_date',
    tooltip: 'createInvoice.tooltips.invoicing_period_start_date',
    id: 'invoicingPeriodStartDate',
    disabled: false,
    hide: false,
    required: values.summaryEinvoice || values.continuousSupply,
    disabledDays: [{ after: new Date() }],
  },
  {
    name: 'invoicingPeriodEndDate',
    type: 'datepicker',
    label: 'createInvoice.invoicing_period_end_date',
    tooltip: 'createInvoice.tooltips.invoicing_period_end_date',
    id: 'invoicingPeriodEndDate',
    disabled: !values.invoicingPeriodStartDate,
    hide: false,
    required: values.summaryEinvoice || values.continuousSupply,
    disabledDays: values.invoicingPeriodStartDate
      ? [
          {
            before: moment(
              values.invoicingPeriodStartDate,
              'DD/MM/YYYY',
            ).toDate(),
          },
        ]
      : [{ after: new Date() }],
  },
];

export const sellerIdentificationInputs = ({
  initialValues,
  language,
  values,
}) => {
  const isSelfBilling = values?.specialBilling === 'Self';

  return [
    {
      name: 'sellerEnglishName',
      type: 'text',
      label: 'createInvoice.seller_name',
      id: 'sellerEnglishName',
      disabled: false,
      hide: false,
      required: true,
      maxlength: FIELDS_MAX_LENGTH.name,
    },
    {
      name: 'sellerVatNumber',
      type: 'vat',
      label: 'createInvoice.vat_registration_number',
      id: 'sellerVatNumber',
      hide: false,
      required: values?.throughRCM === 'Import' ? false : true,
      verifyVat: isSelfBilling,
      minlength: FIELDS_MAX_LENGTH.VAT,
      maxlength: FIELDS_MAX_LENGTH.VAT,
      updateFieldsNames: {
        name: 'sellerEnglishName',
        buildingName: 'sellerBuildingName',
        streetName: 'sellerStreetEn',
        city: 'sellerCity',
        flatNumber: 'sellerFlatNumber',
        block: 'sellerBlock',
      },
    },
    {
      name: 'additionalSellerIdType',
      type: 'select',
      label: 'createInvoice.seller_id_type',
      id: 'additionalSellerIdType',
      disabled: false,
      options: ADDITIONAL_SELLER_ID_TYPES(language),
      hide: false,
      required: true,
    },
    {
      name: 'additionalSellerId',
      type: 'text',
      label: 'createInvoice.seller_id_number',
      tooltip: 'createInvoice.tooltips.seller_identifier',
      id: 'additionalSellerId',
      disabled: false,
      hide: false,
      required: true,
      maxlength: FIELDS_MAX_LENGTH.idNumber,
    },
  ];
};

export const sellerAddressInputs = ({ countries }) => [
  {
    name: 'sellerFlatNumber',
    type: 'text',
    label: 'createInvoice.flat_number',
    id: 'sellerFlatNumber',
    disabled: false,
    hide: false,
    required: true,
    maxlength: 10,
  },
  {
    name: 'sellerBuildingName',
    type: 'text',
    label: 'createInvoice.building_name',
    id: 'sellerBuildingName',
    disabled: false,
    hide: false,
    required: true,
    maxlength: FIELDS_MAX_LENGTH.buildingName,
  },
  {
    name: 'sellerBlock',
    type: 'text',
    label: 'createInvoice.block',
    id: 'sellerBlock',
    disabled: false,
    hide: false,
    required: true,
    maxlength: FIELDS_MAX_LENGTH.block,
  },
  {
    name: 'sellerStreetEn',
    type: 'text',
    label: 'createInvoice.street_name',
    id: 'sellerStreetEn',
    disabled: false,
    hide: false,
    required: true,
    maxlength: FIELDS_MAX_LENGTH.streetName,
  },

  {
    name: 'sellerCity',
    type: 'text',
    label: 'createInvoice.city',
    id: 'sellerCity',
    disabled: false,
    hide: false,
    required: true,
    maxlength: FIELDS_MAX_LENGTH.city,
  },
  {
    name: 'sellerCountryCode',
    type: 'select',
    options: countries,
    label: 'createInvoice.country_code',
    id: 'sellerCountryCode',
    disabled: false,
    hide: false,
    required: true,
  },
];

// BT-044: Buyer Name (BR-BH-43)
export const buyerIdentificationInputs = ({
  language,
  invoiceType,
  values,
  category,
}) => {
  const isSimplified = category === 'simplified';
  const isSummary = values?.summaryEinvoice === true;
  const isSelfBilling = values?.specialBilling === 'Self';
  const isExport = values?.throughRCM === 'Export';

  // BR-BH-43: Buyer name required for full docs OR simplified+summary
  const buyerNameRequired = !isSimplified || (isSimplified && isSummary);

  // BR-BH-44: Self-billing requires VAT
  const buyerVatRequired = isSelfBilling;

  // BR-BH-45: For standard invoices (including exports), either VAT or additional ID required
  // Per validation rules: If VAT not provided, buyer ID must be provided for Full Document and Exports

  return [
    {
      name: 'buyerEnglishName',
      type: 'text',
      label: 'createInvoice.buyer_name',
      placeholderKey: 'createInvoice.buyer_name_placeholder',
      id: 'buyerEnglishName',
      disabled: false,
      hide: false,
      required: buyerNameRequired,
      maxlength: FIELDS_MAX_LENGTH.name,
    },
    {
      name: 'buyerVatNumber',
      type: 'vat',
      label: 'createInvoice.vat_registration_number',
      placeholderKey: 'createInvoice.buyer_vat_number_placeholder',
      tooltip: 'createInvoice.tooltips.buyerVatNumber',
      id: 'buyerVatNumber',
      disabled: false,
      hide: false,
      verifyVat: !isSelfBilling,
      required: buyerVatRequired,
      maxlength: FIELDS_MAX_LENGTH.VAT,
      updateFieldsNames: {
        name: 'buyerEnglishName',
        buildingName: 'buyerBuildingName',
        streetName: 'buyerStreetEn',
        city: 'buyerCity',
        country: 'buyerCountryCode',
        flatNumber: 'buyerFlatNumber',
        block: 'buyerBlock',
      },
    },
    {
      name: 'additionalBuyerIdType',
      type: 'select',
      label: 'createInvoice.additional_buyer_id_type',
      tooltip:
        invoiceType === INVOICE_TYPE.INVOICE
          ? 'createInvoice.tooltips.additionalBuyerIdType'
          : '',
      id: 'additionalBuyerIdType',
      disabled: false,
      options: ADDITIONAL_BUYER_ID_TYPES(language),
      hide: false,
      // BT-046-1: Scheme identifier required if buyer ID is provided
      required:
        (!!values?.additionalBuyerId || !values.buyerVatNumber) && !isExport,
    },
    {
      name: 'additionalBuyerId',
      type: 'text',
      label: 'createInvoice.additional_buyer_id_number',
      placeholderKey: 'createInvoice.additional_buyer_id_number_placeholder',
      id: 'additionalBuyerId',
      disabled: false,
      hide: false,
      // BT-046: Required when ID type is selected, or when identification is required
      required:
        !!values?.additionalBuyerIdType ||
        (!values.buyerVatNumber && !isExport),
      maxlength: FIELDS_MAX_LENGTH.idNumber,
    },
  ];
};

export const buyerAddressInputs = ({ cities, countries, values, category }) => {
  // BR-BH-48: For export scenarios, only country code is required
  const isExport = values?.throughRCM === 'Export';
  const isSimplified = category === 'simplified';
  const isSummary = values?.summaryEinvoice === true;

  // BR-BH-46 & BR-BH-47: Full address required for:
  // 1. Full Documents (standard invoices)
  // 2. Simplified + Summary
  // Exception: Exports only need country code (BR-BH-48)
  const requiresFullAddress =
    (!isSimplified || (isSimplified && isSummary)) && !isExport;

  return [
    // BT-BH-013: Buyer Address Line 4 - Flat/Shop Number (BR-BH-47)
    {
      name: 'buyerFlatNumber',
      type: 'text',
      label: 'createInvoice.flat_number',
      placeholderKey: 'createInvoice.flat_number_placeholder',
      id: 'buyerFlatNumber',
      disabled: false,
      required: requiresFullAddress,
      maxlength: 10,
    },
    // BT-163: Buyer Address Line 3 - Building Name (BR-BH-47)
    {
      name: 'buyerBuildingName',
      type: 'text',
      label: 'createInvoice.building_name',
      placeholderKey: 'createInvoice.building_name_placeholder',
      id: 'buyerBuildingName',
      disabled: false,
      required: requiresFullAddress,
      maxlength: FIELDS_MAX_LENGTH.buildingName,
    },
    // BT-051: Buyer Address Line 2 - Block (BR-BH-47)
    {
      name: 'buyerBlock',
      type: 'text',
      label: 'createInvoice.block',
      placeholderKey: 'createInvoice.block_placeholder',
      id: 'buyerBlock',
      disabled: false,
      required: requiresFullAddress,
      maxlength: FIELDS_MAX_LENGTH.block,
    },
    // BT-050: Buyer Address Line 1 - Street Name (BR-BH-47)
    {
      name: 'buyerStreetEn',
      type: 'text',
      label: 'createInvoice.street_name',
      placeholderKey: 'createInvoice.street_name_placeholder',
      id: 'buyerStreetEn',
      disabled: false,
      required: requiresFullAddress,
      maxlength: FIELDS_MAX_LENGTH.streetName,
    },
    // BT-052: Buyer City (BR-BH-47)
    {
      name: 'buyerCity',
      type: 'text',
      label: 'createInvoice.city',
      placeholderKey: 'createInvoice.city_placeholder',
      id: 'buyerCity',
      disabled: false,
      required: requiresFullAddress,
      maxlength: FIELDS_MAX_LENGTH.city,
    },
    // BT-055: Buyer Country Code (BR-BH-47, BR-BH-48 - Always required)
    {
      name: 'buyerCountryCode',
      type: 'select',
      options: countries,
      label: 'createInvoice.country_code',
      id: 'buyerCountryCode',
      disabled: false,
      hide: false,
      required: isExport || requiresFullAddress,
    },
  ];
};

export const orderReferenceInputs = () => [
  {
    name: 'contractReference',
    type: 'text',
    label: 'createInvoice.contract_reference',
    placeholderKey: 'createInvoice.contract_reference_placeholder',
    id: 'contractReference',
    tooltip: 'createInvoice.tooltips.contractReference',
    disabled: false,
    hide: false,
    required: false,
    maxlength: FIELDS_MAX_LENGTH.contractReference,
  },
  {
    name: 'purchaseOrderNumber',
    type: 'text',
    label: 'createInvoice.purchase_order_number',
    placeholderKey: 'createInvoice.purchase_order_number_placeholder',
    id: 'purchaseOrderNumber',
    tooltip: 'createInvoice.tooltips.purchaseOrderNumber',
    disabled: false,
    hide: false,
    required: false,
    maxlength: FIELDS_MAX_LENGTH.contractReference,
  },
  {
    name: 'salesOrderReference',
    type: 'text',
    label: 'createInvoice.sales_order_reference',
    placeholderKey: 'createInvoice.sales_order_reference_placeholder',
    id: 'salesOrderReference',
    tooltip: 'createInvoice.tooltips.salesOrderReference',
    disabled: false,
    hide: false,
    required: false,
    maxlength: FIELDS_MAX_LENGTH.contractReference,
  },
];

export const handleDecimalKeyPress = (e, maxDecimalPlaces = 3) => {
  const { key, target } = e;
  const allowedPattern = /^[0-9.]$/;

  if (!allowedPattern.test(key)) {
    e.preventDefault();
    return;
  }

  const value = target.value || '';
  const selectionStart = target.selectionStart ?? value.length;
  const selectionEnd = target.selectionEnd ?? selectionStart;
  const dotIndex = value.indexOf('.');

  if (key === '.') {
    const selectedValue = value.slice(selectionStart, selectionEnd);
    if (dotIndex !== -1 && !selectedValue.includes('.')) {
      e.preventDefault();
    }
    return;
  }

  if (dotIndex === -1 || selectionStart <= dotIndex) {
    return;
  }

  const fractionalPart = value.slice(dotIndex + 1);
  const selectedFractionStart = Math.max(selectionStart, dotIndex + 1);
  const selectedFractionEnd = Math.max(selectionEnd, dotIndex + 1);
  const selectedFractionLength = Math.max(
    0,
    selectedFractionEnd - selectedFractionStart,
  );

  if (fractionalPart.length - selectedFractionLength >= maxDecimalPlaces) {
    e.preventDefault();
  }
};

export const lineItemsInputs = ({
  values,
  setFieldValue,
  taxCountry,
  language,
  countryData,
  setFieldTouched,
  validateField,
}) => {
  const selectedCurrency =
    values?.currency || countryData?.currency?.en || 'BHD';
  const localizedCurrency = getLocalizedCurrencyLabel(
    selectedCurrency,
    language,
  );

  return [
    {
      name: 'tempItem.descriptionEn',
      type: 'text',
      label: 'createInvoice.goods_or_service_description',
      tooltip: 'createInvoice.tooltips.tempItem.descriptionEn',
      id: 'descriptionEn',
      disabled: false,
      hide: false,
      required: true,
      maxlength: FIELDS_MAX_LENGTH.description,
      placeholderKey: 'createInvoice.add_item_description_placeholder',
      pattern: '^[a-zA-Z0-9\u0600-\u06FF\\s.,\\-/()@&]*$',
      onChange: (e) => {
        const value = e.target.value;
        setFieldValue('tempItem.descriptionEn', value);
        // Mark as touched to show validation errors
        if (setFieldTouched) {
          setFieldTouched('tempItem.descriptionEn', true, true);
        }
      },
      onBlur: () => {
        // Mark as touched on blur
        if (setFieldTouched) {
          setFieldTouched('tempItem.descriptionEn', true, true);
        }
      },
    },
    {
      name: 'tempItem.unitPrice',
      type: 'text',
      label: 'createInvoice.unit_price',
      tooltip: 'createInvoice.tooltips.tempItem.unitPrice',
      id: 'unitPrice',
      disabled: false,
      hide: false,
      required: true,
      placeholder: `0.000 ${localizedCurrency}`,
      inputMode: 'decimal',
      maxlength: 15,
      onKeyPress: (e) => handleDecimalKeyPress(e),
      onBlur: () => {
        if (setFieldTouched) {
          setFieldTouched('tempItem.unitPrice', true, true);
        }
        onValuesBlur({ item: values.tempItem, setFieldValue });
      },
    },
    {
      name: 'tempItem.quantity',
      type: 'text',
      label: 'createInvoice.quantity',
      tooltip: 'createInvoice.tooltips.tempItem.quantity',
      id: 'quantity',
      disabled: false,
      hide: false,
      required: true,
      placeholderKey: 'createInvoice.quantity_placeholder',
      inputMode: 'numeric',
      onChange: (e) => {
        // Only allow integers - strip out any non-numeric characters including decimals
        const value = e.target.value.replace(/[^0-9]/g, '');
        setFieldValue('tempItem.quantity', value);
        if (setFieldTouched) {
          setFieldTouched('tempItem.quantity', true, true);
        }
      },
      onKeyPress: (e) => {
        // Only allow integers (no decimal point)
        const allowedPattern = /^[0-9]$/;
        if (!allowedPattern.test(e.key)) {
          e.preventDefault();
        }
      },
      maxlength: 15,
      onBlur: () => {
        if (setFieldTouched) {
          setFieldTouched('tempItem.quantity', true, true);
        }
        onValuesBlur({ item: values.tempItem, setFieldValue });
      },
    },
    {
      name: 'tempItem.discountPercentage',
      type: 'text',
      label: 'createInvoice.discount_or_rebate_percentage',
      tooltip: 'createInvoice.tooltips.tempItem.discountAmount',
      id: 'discountPercentage',
      disabled: values?.tempItem?.discountAmount > 0,
      hide: false,
      required: false,
      placeholderKey: 'createInvoice.discount_percentage_placeholder',
      inputMode: 'decimal',
      maxlength: 6,
      onKeyPress: (e) => handleDecimalKeyPress(e, 2),
      onBlur: () => onValuesBlur({ item: values.tempItem, setFieldValue }),
    },
    {
      name: 'tempItem.discountAmount',
      type: 'text',
      label: {
        value: 'createInvoice.discount_or_rebate_amount_qar',
        placeholder: { currency: localizedCurrency },
      },
      tooltip: 'createInvoice.tooltips.tempItem.discountAmount',
      id: 'discountAmount',
      disabled: values?.tempItem?.discountPercentage > 0,
      hide: false,
      required: false,
      placeholder: `0.000 ${localizedCurrency}`,
      inputMode: 'decimal',
      maxlength: 15,
      onKeyPress: (e) => handleDecimalKeyPress(e),
      onBlur: () => onValuesBlur({ item: values.tempItem, setFieldValue }),
    },
    {
      name: 'tempItem.netAmount',
      type: 'display',
      label: {
        value: 'createInvoice.net_amount',
      },
      id: 'netAmount',
      disabled: true,
      hide: false,
      required: false,
    },
    {
      name: 'tempItem.vatRateType',
      type: 'select',
      label: 'createInvoice.tax_code_for_vat',
      id: 'vatRateType',
      disabled: false,
      hide: false,
      required: true,

      options: getTaxTypes('OTA', language), // Exclude the empty option since we have a placeholder
      onChange: (e) =>
        onVatRateChange(e, {
          setFieldValue,
          item: values.tempItem,
          vatTypes: getTaxTypes(taxCountry, 'en'),
        }),
    },
    {
      name: 'tempItem.vatAmount',
      type: 'display',
      label: {
        value: 'createInvoice.vat_amount_qar',
        placeholder: { currency: localizedCurrency },
      },
      tooltip: 'createInvoice.tooltips.tempItem.vatAmount',
      id: 'vatAmount',
      disabled: true,
      hide: false,
      required: false,
    },
    {
      name: 'tempItem.totalAmount',
      type: 'display',
      label: {
        value: 'createInvoice.total_incl_vat',
      },
      id: 'totalAmount',
      disabled: true,
      hide: false,
      required: false,
    },
  ];
};

export const paymentTermsInputs = ({ values, language }) => [
  {
    name: 'paymentMethod',
    type: 'select',
    label: 'createInvoice.payment_method',
    tooltip: 'createInvoice.tooltips.paymentMethod',
    id: 'paymentMethod',
    disabled: false,
    hide: false,
    required: false,
    options: PAYMENT_METHOD(language),
  },
  {
    name: 'accountNumber',
    type: 'text',
    label: 'createInvoice.account_number',
    tooltip: 'createInvoice.tooltips.accountNumber',
    placeholderKey: 'createInvoice.account_number_placeholder',
    id: 'accountNumber',
    disabled: false,
    hide: ![
      'CREDIT_TRANSFER',
      'DEBIT_CARD',
      'CHEQUE',
      'BANKERS_DRAFT',
    ].includes(values.paymentMethod),
    required: true,
    maxlength: FIELDS_MAX_LENGTH.accountNumber,
  },
  {
    name: 'accountName',
    type: 'text',
    label: 'createInvoice.account_name',
    tooltip: 'createInvoice.tooltips.accountName',
    id: 'accountName',
    disabled: false,
    hide: ![
      'CREDIT_TRANSFER',
      'DEBIT_CARD',
      'CHEQUE',
      'BANKERS_DRAFT',
    ].includes(values.paymentMethod),
    placeholderKey: 'createInvoice.account_name_placeholder',
    required: true,
    maxlength: FIELDS_MAX_LENGTH.accountName,
  },
  {
    name: 'bankNameEn',
    type: 'text',
    label: 'createInvoice.bank_name',
    tooltip: 'createInvoice.tooltips.bankNameEn',
    id: 'bankNameEn',
    disabled: false,
    hide: ![
      'CREDIT_TRANSFER',
      'DEBIT_CARD',
      'CHEQUE',
      'BANKERS_DRAFT',
    ].includes(values.paymentMethod),
    placeholderKey: 'createInvoice.bank_name_placeholder',
    required: true,
    maxlength: FIELDS_MAX_LENGTH.bankName,
  },

  {
    name: 'paymentTermsEn',
    type: 'text',
    label: 'createInvoice.payment_terms',
    tooltip: 'createInvoice.tooltips.paymentTermsEn',
    placeholderKey: 'createInvoice.payment_terms_placeholder',
    id: 'paymentTermsEn',
    disabled: false,
    hide: false,
    required: false,
    maxlength: FIELDS_MAX_LENGTH.paymentTerms,
  },
];

export const referenceInputs = ({
  previousInvoices,
  setFieldValue,
  language,
}) => [
  {
    name: 'originalInvoiceReference',
    type: 'select',
    label: 'createInvoice.reference_to_original_invoice',
    id: 'originalInvoiceReference',
    disabled: false,
    hide: false,
    required: true,
    className: 'col-6',
    options: PREVIOUS_INVOICES({ previousInvoices, language }),
    onChange: (e) =>
      setOriginalInvoiceGross({ e, previousInvoices, setFieldValue }),
  },
  {
    name: 'originalGrossAmount',
    type: 'text',
    label: 'createInvoice.gross_amount_of_referenced_invoice',
    id: 'originalGrossAmount',
    disabled: true,
    hide: false,
    required: false,
    className: 'col-6',
  },
  {
    name: 'noteReasonOfIssuance',
    type: 'select',
    label: 'createInvoice.reason_for_credit_debit_note_issuance',
    id: 'noteReasonOfIssuance',
    disabled: false,
    hide: false,
    required: true,
    options: NOTE_ISSUANCE_REASONS(language),
    className: 'col-6',
  },
];

export const notesInputs = () => [
  {
    name: 'notesEn',
    type: 'textarea',
    label: 'createInvoice.notes',
    tooltip: 'createInvoice.tooltips.notes',
    placeholderKey: 'createInvoice.notes_placeholder',
    id: 'notesEn',
    disabled: false,
    hide: false,
    required: false,
    maxlength: FIELDS_MAX_LENGTH.notes,
  },
];

export const logoInput = () => ({
  name: 'logo',
  type: 'file',
  label: '',
  id: 'logo',
  disabled: false,
  hide: false,
  required: false,
  componentProps: {
    tooltip: 'createInvoice.tooltips.logo',
    accept: 'image/jpeg,image/png,image/svg+xml',
    maxFileSize: 2e6,
  },
});

export const specialScenarioInput = () => ({
  name: 'specialScenario',
  type: 'switch',
  label: 'createInvoice.specialScenario',
  tooltip: 'createInvoice.tooltips.specialScenario',
  infoTitle: 'createInvoice.info.specialScenario.title',
  infoDetails: 'createInvoice.info.specialScenario.details',
  id: 'specialScenario',
  disabled: false,
  hide: false,
  required: false,
  component: getInputType('switch'),
  controlProps: {
    className: 'switchInput',
  },
});

export const summaryEinvoiceInput = () => ({
  name: 'summaryEinvoice',
  type: 'check',
  id: 'summaryEinvoice',
  disabled: false,
  hide: false,
  required: false,
  showError: true,
  component: getInputType('check'),
  componentProps: {
    label: 'createInvoice.summaryEinvoice',
  },
});

export const continuousSupplyInput = () => ({
  name: 'continuousSupply',
  type: 'check',
  id: 'continuousSupply',
  disabled: false,
  hide: false,
  required: false,
  showError: true,
  component: getInputType('check'),
  componentProps: {
    label: 'createInvoice.continuousSupply',
  },
});

export const deemedSupplyInput = () => ({
  name: 'deemedSupply',
  type: 'check',
  id: 'deemedSupply',
  disabled: false,
  hide: false,
  required: false,
  showError: true,
  component: getInputType('check'),
  componentProps: {
    label: 'createInvoice.deemedSupply',
  },
});

export const domesticSupplyInput = (category) => ({
  name: 'domesticSupply',
  type: 'check',
  id: 'domesticSupply',
  disabled: category === 'simplified',
  hide: false,
  required: false,
  showError: true,
  component: getInputType('check'),
  componentProps: {
    label: 'createInvoice.domesticSupply',
    disabled: category === 'simplified',
  },
  tooltip:
    category === 'simplified'
      ? 'createInvoice.tooltips.disabled_for_simplified'
      : undefined,
});

export const prepaymentInvoiceInput = () => ({
  name: 'prepaymentInvoice',
  type: 'check',
  id: 'prepaymentInvoice',
  disabled: false,
  hide: false,
  required: false,
  showError: true,
  component: getInputType('check'),
  componentProps: {
    label: 'createInvoice.prepaymentInvoice',
  },
});

export const throughRCMCheckInput = (category, setFieldValue) => ({
  name: 'throughRCMCheck',
  type: 'check',
  id: 'throughRCMCheck',
  showError: true,
  disabled: category === 'simplified',
  hide: false,
  required: false,
  component: getInputType('check'),
  componentProps: {
    label: 'createInvoice.throughRCMCheck',
    disabled: category === 'simplified',
    onChange: (checked) => {
      if (checked) {
        setFieldValue('throughRCM', 'Export');
      }
    },
  },
  tooltip:
    category === 'simplified'
      ? 'createInvoice.tooltips.disabled_for_simplified'
      : undefined,
});

export const throughRCMInput = (category) => ({
  name: 'throughRCM',
  type: 'radio',
  id: 'throughRCM',
  disabled: category === 'simplified',
  hide: false,
  required: false,
  component: getInputType('radio'),
  componentProps: {
    disabled: category === 'simplified',
    options: [
      { value: 'Export', label: 'createInvoice.export' },
      { value: 'Import', label: 'createInvoice.import' },
    ],
  },
  tooltip:
    category === 'simplified'
      ? 'createInvoice.tooltips.disabled_for_simplified'
      : undefined,
});

export const specialBillingCheckInput = (category, setFieldValue) => ({
  name: 'specialBillingCheck',
  type: 'check',
  id: 'specialBillingCheck',
  showError: true,
  disabled: category === 'simplified',
  hide: false,
  required: false,
  component: getInputType('check'),
  componentProps: {
    label: 'createInvoice.specialBillingCheck',
    onChange: (checked) => {
      if (checked) {
        if (category === 'simplified') {
          setFieldValue('specialBilling', 'Third');
        } else {
          setFieldValue('specialBilling', 'Self');
        }
      }
    },
  },
  tooltip: 'createInvoice.tooltips.disabled_for_simplified',
});

export const specialBillingInput = (category) => ({
  name: 'specialBilling',
  type: 'radio',
  id: 'specialBilling',
  disabled: category === 'simplified',
  hide: false,
  required: false,
  component: getInputType('radio'),
  componentProps: {
    disabled: category === 'simplified',
    options: [
      {
        value: 'Self',
        label: 'createInvoice.self-billing',
        disabled: category === 'simplified',
      },
      { value: 'Third', label: 'createInvoice.third-party-billing' },
    ],
  },
  tooltip:
    category === 'simplified'
      ? 'createInvoice.tooltips.disabled_for_simplified'
      : undefined,
});

export const supplyDateInput = () => ({
  name: 'supplyDate',
  type: 'datepicker',
  label: 'createInvoice.supplyDate',
  id: 'supplyDate',
  disabled: false,
  hide: false,
  required: false,
  tooltip: 'createInvoice.tooltips.supplyDate',
  component: getInputType('datepicker'),
  componentProps: {
    showClearIcon: true,
  },
  labelProps: {
    className: 'flex-1 justify-content-between',
  },
  controlProps: {
    className:
      'flex-row text-nowrap gap-2 justify-content-between max-w-22rem flex-nowrap',
  },
});

export const paymentDateInput = () => ({
  name: 'paymentDate',
  type: 'datepicker',
  label: 'createInvoice.paymentDate',
  id: 'paymentDate',
  tooltip: 'createInvoice.tooltips.paymentDate',
  disabled: false,
  hide: false,
  required: false,
  component: getInputType('datepicker'),
  componentProps: {
    showClearIcon: true,
  },
  controlProps: {
    className:
      'flex-row text-nowrap gap-2 justify-content-between max-w-22rem flex-nowrap',
  },
});

export const precedingInvoiceNumberInput = ({ invoiceType }) => ({
  name: 'precedingInvoiceNumber',
  type: 'text',
  label: 'createInvoice.precedingInvoiceNumber',
  id: 'precedingInvoiceNumber',
  tooltip: 'createInvoice.tooltips.precedingInvoiceNumber',
  disabled: false,
  hide: invoiceType === INVOICE_TYPE.INVOICE,
  required: true,
  component: getInputType('text'),
  maxlength: 50,
  minLength: 1,
});

export const precedingInvoiceIssueDateInput = ({ invoiceType }) => ({
  name: 'precedingInvoiceIssueDate',
  type: 'datepicker',
  label: 'createInvoice.precedingInvoiceIssueDate',
  id: 'precedingInvoiceIssueDateInput',
  tooltip: 'createInvoice.tooltips.precedingInvoiceIssueDate',
  disabled: false,
  hide: invoiceType === INVOICE_TYPE.INVOICE,
  required: true,
  component: getInputType('datepicker'),
});

export const noteIssuingReasonInput = ({ invoiceType }) => ({
  name: 'noteIssuingReason',
  type: 'select',
  label: 'createInvoice.noteIssuingReason',
  id: 'noteIssuingReason',
  tooltip: 'createInvoice.tooltips.noteIssuingReason',
  disabled: false,
  hide: invoiceType === INVOICE_TYPE.INVOICE,
  required: true,
  component: getInputType('select'),
  options: NOTE_ISSUANCE_REASONS(i18n.language),
});

export const discountInput = ({ setShowDiscountDialog }) => ({
  name: 'einvoiceLevelDiscount',
  component: getInputType('switch'),
  label: 'createInvoice.einvoiceLevelDiscount',
  infoTitle: 'createInvoice.info.einvoiceLevelDiscount.title',
  infoDetails: 'createInvoice.info.einvoiceLevelDiscount.details',
  controlProps: {
    className: 'switchInput label-border flex-1 mw-300',
    labelProps: {
      className: 'flex-1',
    },
  },
  componentProps: {
    onChange: (checked) => setShowDiscountDialog(checked),
  },
});

export const paidAmountInput = ({ setShowPaymentDialog }) => ({
  name: 'paidAmountSwitch',
  component: getInputType('switch'),
  label: 'createInvoice.paidAmount',
  infoTitle: 'createInvoice.info.paidAmount.title',
  infoDetails: 'createInvoice.info.paidAmount.details',
  showError: false,
  controlProps: {
    className: 'switchInput label-border flex-1 mw-300',
    labelProps: {
      className: 'flex-1',
    },
  },

  componentProps: {
    onChange: (checked) => setShowPaymentDialog(checked),
  },
});

export const roundingAmountSwitchInput = ({ setFieldValue }) => ({
  name: 'roundingAmountSwitch',
  component: getInputType('switch'),
  label: 'createInvoice.roundingAmountSwitch',
  tooltip: 'createInvoice.tooltips.roundingAmountSwitch',
  componentProps: {
    onChange: (checked) => {
      if (!checked) {
        setFieldValue('roundingAmount', '');
      }
    },
  },
  controlProps: {
    className: 'switchInput label-border flex-1 mw-300',
    labelProps: {
      className: 'flex-1',
    },
  },
});

export const roundingAmountInput = ({ currency, language = 'en' }) => ({
  name: 'roundingAmount',
  component: getInputType('currency'),
  tooltip: 'createInvoice.tooltips.roundingAmountSwitch',
  componentProps: {
    maxLength: 29,
    suffix: ` ${getLocalizedCurrencyLabel(currency, language)}`,
  },
  controlProps: {
    className: 'align-items-end',
    freeInput: false,
  },
});

export const otherDates = ({ values }) => [
  {
    name: 'invoicingPeriodStartDate',
    type: 'datepicker',
    component: getInputType('datepicker'),
    label: 'createInvoice.invoicing-period-start-date',
    tooltip: 'createInvoice.tooltips.invoicingPeriodStartDate',
    id: 'invoicingPeriodStartDate',
    disabled: false,
    hide: false,
    required: false,
  },
  {
    name: 'invoicingPeriodEndDate',
    type: 'datepicker',
    component: getInputType('datepicker'),
    label: 'createInvoice.invoicing-period-end-date',
    tooltip: 'createInvoice.tooltips.invoicingPeriodEndDate',
    id: 'invoicingPeriodEndDate',
    disabled: false,
    hide: false,
    required: values.summaryEinvoice,
    disabledDays: [
      {
        before: moment(values.invoicingPeriodStartDate, 'DD/MM/YYYY').toDate(),
      },
    ],
  },
];

export const thirdPartyIdentificationInputs = ({
  initialValues,
  language,
  values,
}) => [
  {
    name: 'thirdPartyEnglishName',
    type: 'text',
    label: 'createInvoice.thirdParty_name',
    id: 'thirdPartyEnglishName',
    disabled: false,
    hide: false,
    required: values.specialBillingCheck && values.specialBilling === 'Third',
    maxlength: FIELDS_MAX_LENGTH.name,
  },
  {
    name: 'thirdPartyVatNumber',
    type: 'vat',
    label: 'createInvoice.vat_registration_number',
    id: 'thirdPartyVatNumber',
    disabled: false,
    hide: false,
    verifyVat: true,
    required: values.specialBillingCheck && values.specialBilling === 'Third',
    maxlength: 15,
    forceVerifyVat: true,
    updateFieldsNames: {
      name: 'thirdPartyEnglishName',
      buildingName: 'thirdPartyBuildingName',
      streetName: 'thirdPartyStreetEn',
      city: 'thirdPartyCity',
      country: 'thirdPartyCountryCode',
      flatNumber: 'thirdPartyFlatNumber',
      block: 'thirdPartyBlock',
      tin: 'thirdPartyTinNumber',
    },
  },
  {
    name: 'thirdPartyTinNumber',
    type: 'text',
    label: 'createInvoice.third_party_tin',
    id: 'thirdPartyTinNumber',
    disabled: false,
    hide: false,
    required: values.specialBillingCheck && values.specialBilling === 'Third',
    maxlength: FIELDS_MAX_LENGTH.thirdPartyTinNumber,
  },
];

export const thirdPartyAddressInputs = ({ cities, countries, values }) => [
  // BT-BH-034: Third Party Address Line 1 - Street Name (BR-BH-42)
  {
    name: 'thirdPartyStreetEn',
    type: 'text',
    label: 'createInvoice.street_name',
    id: 'thirdPartyStreetEn',
    disabled: false,
    hide: false,
    required: values.specialBillingCheck && values.specialBilling === 'Third',
    maxlength: FIELDS_MAX_LENGTH.streetName,
  },
  // BT-BH-035: Third Party Address Line 2 - Block (BR-BH-42)
  {
    name: 'thirdPartyBlock',
    type: 'text',
    label: 'createInvoice.block',
    id: 'thirdPartyBlock',
    disabled: false,
    hide: false,
    required: values.specialBillingCheck && values.specialBilling === 'Third',
    maxlength: FIELDS_MAX_LENGTH.block,
  },
  // BT-BH-036: Third Party Address Line 3 - Building Name (BR-BH-42)
  {
    name: 'thirdPartyBuildingName',
    type: 'text',
    label: 'createInvoice.building_name',
    id: 'thirdPartyBuildingName',
    disabled: false,
    hide: false,
    required: values.specialBillingCheck && values.specialBilling === 'Third',
    maxlength: FIELDS_MAX_LENGTH.buildingName,
  },
  // BT-BH-037: Third Party Address Line 4 - Flat/Shop No (BR-BH-42)
  {
    name: 'thirdPartyFlatNumber',
    type: 'text',
    label: 'createInvoice.flat_number',
    id: 'thirdPartyFlatNumber',
    disabled: false,
    hide: false,
    required: values.specialBillingCheck && values.specialBilling === 'Third',
    maxlength: 10,
  },
  // BT-BH-038: Third Party City (BR-BH-42)
  {
    name: 'thirdPartyCity',
    type: 'text',
    label: 'createInvoice.city',
    id: 'thirdPartyCity',
    disabled: false,
    hide: false,
    required: values.specialBillingCheck && values.specialBilling === 'Third',
    maxlength: FIELDS_MAX_LENGTH.city,
  },
  // BT-BH-041: Third Party Country Code (BR-BH-42)
  {
    name: 'thirdPartyCountryCode',
    type: 'select',
    options: countries,
    label: 'createInvoice.country_code',
    id: 'thirdPartyCountryCode',
    disabled: false,
    hide: false,
    required: values.specialBillingCheck && values.specialBilling === 'Third',
  },
];

export const currencyInput = (language = 'en') => ({
  name: 'currency',
  component: getInputType('select'),
  type: 'select',
  label: 'createInvoice.currency',
  tooltip: 'createInvoice.tooltips.currency',
  id: 'currency',
  disabled: false,
  hide: false,
  required: true,
  componentProps: {
    options: CURRENCY_LIST.map((option) => ({
      ...option,
      label: language.startsWith('ar')
        ? `${option.value} - ${getLocalizedCurrencyLabel(
            option.value,
            language,
          )}`
        : option.label,
    })),
  },
});

export const exchangeRateInput = (currency) => ({
  name: 'exchangeRate',
  type: 'text',
  component: getInputType('text'),
  label: 'createInvoice.exchange_rate',
  tooltip: 'createInvoice.tooltips.exchange_rate',
  id: 'exchangeRate',
  disabled: false,
  hide: !currency || currency === 'BHD',
  required: currency && currency !== 'BHD',
  maxlength: FIELDS_MAX_LENGTH.exchangeRate,
});

export const zeroRatingReasonInput = ({ values, language }) => ({
  name: 'zeroRatingReasonInput',
  type: 'select',
  label: 'createInvoice.zero_exemption_reason_code',
  tooltip: 'createInvoice.tooltips.tempItem.exemptionReasonCode',
  id: 'zeroRatingReasonInput',
  disabled: false,
  hide: !values?.items?.some((item) => ['Z'].includes(item?.vatRateType)),
  required: values?.items?.some((item) => ['Z'].includes(item?.vatRateType)),
  options: ZERO_RATING_REASON_CODES(language),
});

export const exemptionReasonInput = ({ values, language }) => ({
  name: 'exemptionReasonCode',
  type: 'select',
  label: 'createInvoice.exemption_reason_code',
  tooltip: 'createInvoice.tooltips.tempItem.exemptionReasonCode',
  id: 'exemptionReasonCode',
  disabled: false,
  hide: !values?.items?.some((item) => ['E'].includes(item?.vatRateType)),
  required: values?.items?.some((item) => ['E'].includes(item?.vatRateType)),
  options: EXEMPTION_REASON_CODES(language),
});

export const outOfScopeReasonInput = ({ values, language }) => ({
  name: 'outOfScopeReasonInput',
  type: 'select',
  label: 'createInvoice.out_of_scope_reason_code',
  tooltip: 'createInvoice.tooltips.tempItem.exemptionReasonCode',
  id: 'outOfScopeReasonInput',
  disabled: false,
  hide: !values?.items?.some((item) => ['O'].includes(item?.vatRateType)),
  required: values?.items?.some((item) => ['O'].includes(item?.vatRateType)),
  options: OUT_OF_SCOPE_REASON_CODES(language),
});
